/**
 * TrackingConfigCard Component
 *
 * Blade / installation-kit tracking config for one org scope, shared by the
 * Company and Reseller panels. Backed by the TrackingConfig table: a row is an
 * explicit on/off statement at this scope; "Inherit" means no row. Config
 * detail (start date, cadence) only appears once a feature is explicitly
 * enabled, and saves are blocked until every visible value passes integrity
 * validation. Saves immediately through the tracking API with its own Save
 * button — not part of the panel's draft/save flow.
 */

import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Grid, Typography } from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';

import {
    InfoCard,
    SelectField,
    TextField,
    DateInput,
    Button,
    colors,
    typography as typographyTokens,
} from '@components/design-system';
import { useTrackingConfigQuery, usePutTrackingConfigMutation } from '@features/redux/apis/org';
import { useAlert } from '@hooks/useAlert';
import { TrackingFeature } from '@api/org';

import type { TrackingConfigRow, TrackingScopeType } from '@api/org';

type EnabledChoice = 'inherit' | 'true' | 'false';

type FeatureFormState = {
    enabled: EnabledChoice;
    startDate: string; // 'YYYY-MM-DD'
    cadence: string;
};

const FEATURE_LABEL: Record<TrackingFeature, string> = {
    [TrackingFeature.Blade]: 'Blade Tracking',
    [TrackingFeature.Kit]: 'Kit Tracking',
};

const DEFAULT_CADENCE: Record<TrackingFeature, string> = {
    [TrackingFeature.Blade]: '1,200',
    [TrackingFeature.Kit]: '800',
};

const today = (): string => dayjs().format('YYYY-MM-DD');

const formFromRow = (row: TrackingConfigRow | undefined): FeatureFormState => ({
    enabled: row ? (String(row.Enabled) as EnabledChoice) : 'inherit',
    // StartDate is a calendar date stored as UTC midnight; slice the ISO string
    // instead of parsing as an instant, or local timezones shift it a day back
    startDate: row?.StartDate ? row.StartDate.slice(0, 10) : today(),
    cadence: row?.CadenceCuts != null ? String(row.CadenceCuts) : '',
});

/** Blank falls to the default cadence; anything typed must be a positive whole number. */
const cadenceValid = (value: string): boolean => {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    return /^\d+$/.test(trimmed) && Number(trimmed) > 0;
};

/** Parseable and not in the future — backdating is allowed (counts existing cuts). */
const startDateValid = (value: string): boolean => {
    const parsed = dayjs(value, 'YYYY-MM-DD');
    return parsed.isValid() && !parsed.isAfter(dayjs(), 'day');
};

/** Only fields that would actually save count toward dirtiness (config is irrelevant unless Enabled). */
const featureDirty = (row: TrackingConfigRow | undefined, form: FeatureFormState): boolean => {
    const savedForm = formFromRow(row);
    if (savedForm.enabled !== form.enabled) return true;
    if (form.enabled !== 'true') return false;
    return form.cadence.trim() !== savedForm.cadence || form.startDate !== savedForm.startDate;
};

type TrackingConfigCardProps = {
    scopeType: TrackingScopeType;
    scopeId: number;
    canEdit: boolean;
    /** What "Inherit" falls back to at this level, e.g. 'Inherit from company' or 'Default (off)'. */
    inheritLabel: string;
};

export const TrackingConfigCard: React.FC<TrackingConfigCardProps> = ({
    scopeType,
    scopeId,
    canEdit,
    inheritLabel,
}) => {
    const { data: rows } = useTrackingConfigQuery({ scopeType, scopeId });
    const [putTrackingConfig, { isLoading: saving }] = usePutTrackingConfigMutation();
    const { setAlert } = useAlert();

    const rowByFeature = useMemo(
        () =>
            (rows ?? []).reduce((byFeature, row) => {
                byFeature[row.Feature] = row;
                return byFeature;
            }, {} as Partial<Record<TrackingFeature, TrackingConfigRow>>),
        [rows],
    );

    const [forms, setForms] = useState<Record<TrackingFeature, FeatureFormState>>({
        [TrackingFeature.Blade]: formFromRow(undefined),
        [TrackingFeature.Kit]: formFromRow(undefined),
    });

    useEffect(() => {
        setForms({
            [TrackingFeature.Blade]: formFromRow(rowByFeature[TrackingFeature.Blade]),
            [TrackingFeature.Kit]: formFromRow(rowByFeature[TrackingFeature.Kit]),
        });
    }, [rowByFeature]);

    const setFeatureForm = (feature: TrackingFeature, changes: Partial<FeatureFormState>) =>
        setForms((previous) => ({ ...previous, [feature]: { ...previous[feature], ...changes } }));

    const enabledOptions = [
        { value: 'inherit', label: inheritLabel },
        { value: 'true', label: 'Enabled' },
        { value: 'false', label: 'Disabled' },
    ];

    // Only values that will actually be saved gate the Save button
    const isValid = Object.values(forms).every(
        (form) => form.enabled !== 'true' || (cadenceValid(form.cadence) && startDateValid(form.startDate)),
    );

    // Greyed out until something differs from what's saved; resyncs after save via the refetch
    const isDirty = [TrackingFeature.Blade, TrackingFeature.Kit].some((feature) =>
        featureDirty(rowByFeature[feature], forms[feature]),
    );

    const handleSave = async () => {
        const dirtyFeatures = [TrackingFeature.Blade, TrackingFeature.Kit].filter((feature) =>
            featureDirty(rowByFeature[feature], forms[feature]),
        );

        const results = await Promise.all(
            dirtyFeatures.map((feature) => {
                const form = forms[feature];
                const enabled = form.enabled === 'inherit' ? null : form.enabled === 'true';
                const cadence = form.cadence.trim();

                return putTrackingConfig({
                    config: {
                        Feature: feature,
                        ScopeType: scopeType,
                        ScopeID: scopeId,
                        Enabled: enabled,
                        CadenceCuts: enabled === true && cadence !== '' ? Number(cadence) : null,
                        StartDate: enabled === true ? form.startDate : null,
                    },
                });
            }),
        );

        const failed = results.some((result) => 'error' in result);
        if (failed) {
            setAlert('Failed to save tracking settings', 'error');
        } else {
            setAlert('Tracking settings saved', 'success');
        }
    };

    const featureBlock = (feature: TrackingFeature) => {
        const form = forms[feature];
        const showConfig = form.enabled === 'true';
        const showCadenceError = showConfig && !cadenceValid(form.cadence);
        const showDateError = showConfig && !startDateValid(form.startDate);

        return (
            <React.Fragment key={feature}>
                <Grid item xs={12} sm={4}>
                    <SelectField
                        label={FEATURE_LABEL[feature]}
                        value={form.enabled}
                        options={enabledOptions}
                        onChange={(value) => setFeatureForm(feature, { enabled: value as EnabledChoice })}
                        disabled={!canEdit}
                    />
                </Grid>
                {showConfig ? (
                    <>
                        <Grid item xs={12} sm={4}>
                            <DateInput
                                label='Counting cuts since'
                                value={form.startDate}
                                max={today()}
                                disabled={!canEdit}
                                error={showDateError}
                                helperText={showDateError ? 'Pick a date that is today or earlier' : undefined}
                                fullWidth
                                onChange={(value) => setFeatureForm(feature, { startDate: value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            {/* errorMessage only while invalid: DS TextField treats its presence as the error state */}
                            <TextField
                                label='Cadence (cuts)'
                                value={form.cadence}
                                placeholder={`Default ${DEFAULT_CADENCE[feature]}`}
                                error={showCadenceError}
                                errorMessage={showCadenceError ? 'Enter a positive whole number of cuts' : undefined}
                                disabled={!canEdit}
                                onChange={(value) => setFeatureForm(feature, { cadence: value })}
                            />
                        </Grid>
                    </>
                ) : (
                    <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[400],
                                fontStyle: 'italic',
                                pb: '10px',
                            }}>
                            No configuration until enabled
                        </Typography>
                    </Grid>
                )}
            </React.Fragment>
        );
    };

    return (
        <InfoCard
            title='Blade & Kit Tracking'
            icon={<BuildOutlinedIcon />}
            note='Blade tracking drives the kiosk blade warning and blade refill emails; kit tracking drives installation kit emails. Cut counting starts from the date you set; backdating counts cuts already made.'
            actions={
                canEdit ? (
                    <Button
                        variant='primary'
                        disabled={!isValid || !isDirty || saving}
                        loading={saving}
                        onClick={handleSave}>
                        Save tracking
                    </Button>
                ) : undefined
            }>
            <Grid container spacing={2}>
                {featureBlock(TrackingFeature.Blade)}
                {featureBlock(TrackingFeature.Kit)}
            </Grid>
            <Box sx={{ pt: 1.5 }}>
                <Typography sx={{ fontSize: typographyTokens.fontSize.xs, color: colors.neutral[500] }}>
                    Cadence left blank uses the default (blade 1,200 cuts, kit 800 cuts).
                </Typography>
            </Box>
        </InfoCard>
    );
};

export default TrackingConfigCard;
